// usePreviewThumbnailLoop — Classic / Modern プレビューの共有 rAF ループ
// (feature/preview-perf 2026-06-13).
//
// ── なぜ存在するか (consult 2026-06-13) ───────────────────────────────────────
// 旧実装 (PreviewOverlay.tsx / ModernPreviewPanel.tsx) は **毎フレーム (rAF 60fps)
// 無条件に** メインキャンバス全体の多段 mipmap 縮小 + per-pixel 細線ブースト
// readback (`renderPreviewThumbnail` 旧版) を実行していた。変更検知ゼロで 60fps
// 全面ダウンスケールし続けるため、メインスレッド予算を食い (a) ブラシカーソル
// (pointermove) のカクつき、(b) 表示範囲枠ドラッグの遅延、(c) 描画中の重さ を
// 引き起こす真因だった。Modern はプレビュー常時表示なので常に発症、Classic は
// ポップアップを開くと同様。
//
// 本フックは Classic / Modern 両方の rAF ループを 1 箇所に括り出し (= 二重実装の
// 解消)、以下で負荷を最小化する:
//
//   1. サムネ再生成を **低レートにスロットル** (PREVIEW_THUMB_FPS ≈ 6fps)。
//      ホバー/静止中でもこの低レートなのでカーソルのフレーム予算を奪わない。
//   2. ローカル操作中は再生成を **サスペンド** (suspended)。自分のストローク中・
//      パン中はサムネ drawImage を走らせず、手を止めた次のスロットル機会で 1 回
//      追いつく。
//   3. 表示範囲枠 (viewport rect) はサムネ再生成と **分離**。キャッシュ済みサムネ
//      (オフスクリーン canvas) を毎フレーム安く blit → その上に枠を stroke するだけ。
//      枠はスクロール/ズーム/反転が変わった時に追従 (= パン中もここは走ってよい)。
//   4. 完全静止 (操作なし・スクロール/ズーム不変・スロットル間隔内・枠不変) なら
//      毎フレーム何もしない (= rAF は回るが描画は skip)。
//
// engine / protocol には触れない。canvasRef を drawImage source に縮小コピーする
// だけ (= 表示専用、§9.7 共有層)。

import { useEffect, useRef } from 'react';

import { type CanvasViewMapper, measureSlot } from '../../../infrastructure/drawing/viewport.js';
import {
  computePreviewViewportShape,
  type PreviewThumbnailSize,
  type PreviewViewportShape,
  renderPreviewThumbnail,
  shouldRegenThumbnail,
  strokePreviewViewportRect,
} from '../classic/previewOverlay.js';

export interface UsePreviewThumbnailLoopParams {
  /** 出力先サムネ canvas (画面に見える方). */
  readonly thumbCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  /** 縮小元 main 描画 canvas. */
  readonly canvasRef: React.RefObject<HTMLCanvasElement | null>;
  /**
   * 表示枠 (= `overflow: hidden` の `<main>`). `clientWidth/Height` (= 表示
   * 範囲サイズの近似、他の呼び出し箇所と同じ精度) だけを読む — 2026-09-29
   * viewport transform 移行 (決定5) 以降、pan 位置は scroll ではなく
   * `viewportRef` から読む。
   */
  readonly wrapperRef: React.RefObject<HTMLDivElement | null>;
  /**
   * 表示変換の窓口 (= `useRoomChrome.viewMapper`)。`{tx,ty}` / zoom / flip / 回転 (canvas-rotation
   * 段取り 2) を毎フレーム最新で読む (高頻度に変わる = ドラッグ pan / ピンチ / ひねりでも
   * React の再レンダリングを挟まない、旧 `wrapper.scrollLeft/Top` 直読みと同じ perf 特性)。
   * 視野枠は `visibleCanvasQuad` から作るので、回転中は傾いた四角形になる。
   */
  readonly viewMapper: CanvasViewMapper;
  /** サムネ寸法 + scale (= computePreviewThumbnailSize). 変化で effect 再構築。 */
  readonly thumb: PreviewThumbnailSize;
  /**
   * ローカル操作中 (= サムネ再生成を止めるべきか) を返す getter。
   *
   * 「自分のストローク中」「パン中 (chrome.panActive)」のいずれかで true を返す
   * よう caller が合成する。毎フレーム呼ばれるので副作用なし・軽量に保つこと。
   * 高頻度に変わる値 (ref 由来) を effect 再構築なしに読むため、関数経由で渡す。
   * 未指定なら常にサスペンドしない (= 低レートスロットルのみ)。
   */
  readonly isSuspended?: () => boolean;
}

/**
 * プレビューのサムネ + 表示範囲枠を rAF ループで描く共有フック。
 *
 * - サムネ画像は **オフスクリーン cache canvas** に低レート (スロットル) + 操作中
 *   サスペンドで焼き、毎フレームはその cache を表示 canvas へ blit するだけ。
 * - 表示範囲枠は cache blit の上に毎フレーム安く stroke (スクロール追従)。
 * - DPR はサムネ再生成時に読み直して cache の backing を追従させる (別 DPI モニタ移動)。
 */
export const usePreviewThumbnailLoop = ({
  thumbCanvasRef,
  canvasRef,
  wrapperRef,
  viewMapper,
  thumb,
  isSuspended,
}: UsePreviewThumbnailLoopParams): void => {
  // 高頻度で変わる値 (isSuspended / viewMapper) は ref で hot-swap し、effect の
  // 再構築 (= rAF 張り直し) を thumb 変化時だけに限定する。
  const viewMapperRef = useRef(viewMapper);
  viewMapperRef.current = viewMapper;
  const isSuspendedRef = useRef(isSuspended);
  isSuspendedRef.current = isSuspended;

  useEffect(() => {
    let raf = 0;
    let running = true;

    // オフスクリーン cache canvas (= サムネ画像のみ。枠は含めない)。低レートで
    // ここへ焼き、表示 canvas へは毎フレーム blit する。lazy 生成して使い回す。
    let cacheCanvas: HTMLCanvasElement | null = null;
    let cacheDpr = 1;
    let lastThumbAt: number | null = null;
    let cacheReady = false;

    // 直近に描いた枠 + cache 状態。同一なら表示再描画を skip して完全静止で no-op。
    let lastRectKey = '';
    let lastBlitDpr = -1;

    const getCache = (): HTMLCanvasElement => {
      if (cacheCanvas === null) cacheCanvas = document.createElement('canvas');
      return cacheCanvas;
    };

    const tick = (): void => {
      if (!running) return;
      raf = requestAnimationFrame(tick);

      const thumbCanvas = thumbCanvasRef.current;
      const mainCanvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (thumbCanvas === null || mainCanvas === null || wrapper === null) return;

      const now =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now();
      const suspended = isSuspendedRef.current?.() ?? false;

      // ── 1. サムネ画像の再生成 (低レートスロットル + 操作中サスペンド)。 ──
      // 手を止めた / パン終了後の次のスロットル機会で 1 回追いつく。
      if (shouldRegenThumbnail(now, lastThumbAt, undefined, suspended)) {
        const dpr =
          typeof window !== 'undefined' && window.devicePixelRatio > 0
            ? window.devicePixelRatio
            : 1;
        try {
          const r = renderPreviewThumbnail(getCache(), mainCanvas, thumb.width, thumb.height, dpr);
          cacheDpr = r.dpr;
          cacheReady = true;
          lastThumbAt = now;
          // cache が更新されたので表示も必ず描き直す (blit 強制)。
          lastRectKey = '';
          lastBlitDpr = -1;
        } catch {
          // defensive: main が一時的に 0x0 等で drawImage が投げたら次 frame で recover。
        }
      }

      // cache がまだ 1 度も焼けていなければ表示しようがないので抜ける。
      if (!cacheReady || cacheCanvas === null) return;

      // ── 2. 表示範囲枠 (= viewport rect) の計算。サムネと分離して安く追従。 ──
      // 回転 0 は従来どおり clientWidth/Height (見た目を段取り 1 以前と一致させる)、回転中は
      // padding を除いた実寸 (傾いた枠の頂点は寸法に敏感なので正確に)。
      const v = viewMapperRef.current.getTransform();
      const slot = v.rotation === 0 ? null : measureSlot(wrapper);
      const shape: PreviewViewportShape = computePreviewViewportShape(
        thumb,
        v,
        slot?.slotWidth ?? wrapper.clientWidth,
        slot?.slotHeight ?? wrapper.clientHeight,
      );
      const rect = shape.rect;

      // ── 3. 完全静止なら表示再描画も skip (= 毎フレーム何もしない)。 ──
      // cache 更新時は lastRectKey='' に倒してあるので必ず再描画。枠 (scroll/zoom/
      // flip) が前フレームと同一かつ DPR 不変なら blit すら省く。
      const rectKey = `${rect.x.toFixed(2)},${rect.y.toFixed(2)},${rect.w.toFixed(2)},${rect.h.toFixed(2)}${
        shape.quad === null
          ? ''
          : `|${shape.quad.points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(';')}`
      }`;
      const displayDpr =
        typeof window !== 'undefined' && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
      if (rectKey === lastRectKey && displayDpr === lastBlitDpr) return;
      lastRectKey = rectKey;
      lastBlitDpr = displayDpr;

      const ctx = thumbCanvas.getContext('2d');
      if (ctx === null) return;

      // 表示 canvas の backing を cache (= サムネ物理寸法) に合わせる。
      if (thumbCanvas.width !== cacheCanvas.width) thumbCanvas.width = cacheCanvas.width;
      if (thumbCanvas.height !== cacheCanvas.height) thumbCanvas.height = cacheCanvas.height;

      // ── 3a. キャッシュ済みサムネを 1:1 blit (= 安い copy、再ダウンスケール無し)。 ──
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, thumbCanvas.width, thumbCanvas.height);
      try {
        ctx.drawImage(cacheCanvas, 0, 0);
      } catch {
        // defensive: cache が一時的に 0x0 等。次 frame で recover。
        return;
      }

      // ── 3b. 表示範囲枠を CSS px 座標系で重ねる (物理 1px、crisp)。 ──
      strokePreviewViewportRect(ctx, rect, cacheDpr, shape.quad);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
    };
    // thumb (寸法) が変わったときだけループを張り直す。viewMapper/isSuspended は
    // ref で hot-swap するので deps に含めない (= rAF を毎回張り直さない)。
  }, [thumbCanvasRef, canvasRef, wrapperRef, thumb]);
};
